import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useRevalidator } from 'react-router-dom';
import type { ZodIssue } from 'zod';
import { Button, Input, Modal } from '@immo/ui-kit';
import { CreateAdminBodySchema } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { FormField } from '@/components/form';

type Field = 'fullName' | 'email';

type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = { fullName: null, email: null };

function fieldFromPath(path: ReadonlyArray<PropertyKey>): Field | undefined {
  const head = path[0];

  if (head === 'fullName') return 'fullName';

  if (head === 'email') return 'email';

  return undefined;
}

function issueKey(field: Field, issue: ZodIssue, rawInput: string): string {
  if (field === 'email') return rawInput.trim().length === 0 ? 'required' : 'invalid';

  if (issue.code === 'too_small') return 'required';

  if (issue.code === 'too_big') return 'too-long';

  return 'invalid';
}

function labelKey(field: Field): string {
  return field === 'fullName' ? 'full-name' : 'email';
}

export function NewAdminPage() {
  const t = useTranslations('pages.admin.admins.create');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();

  const clearError = (key: Field) => setErrors((prev) => (prev[key] === null ? prev : { ...prev, [key]: null }));

  const close = () => {
    if (submitting) return;
    navigate({ pathname: '/admins', search: location.search });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const rawByField: Readonly<Record<Field, string>> = { fullName, email };
    const parsed = CreateAdminBodySchema.safeParse({ fullName, email });

    if (!parsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      for (const issue of parsed.error.issues) {
        const field = fieldFromPath(issue.path);

        if (field !== undefined && next[field] === null) {
          next[field] = t(`errors.${labelKey(field)}.${issueKey(field, issue, rawByField[field])}`);
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminAdmins.create({ body: parsed.data });

      if (result.ok) {
        revalidator.revalidate();
        navigate('/admins');

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

      if (code === 'admin.admin.duplicate-email') {
        setErrors((prev) => ({ ...prev, email: t('duplicate-error') }));
      } else if (result.status === 403) {
        setGenericError(t('forbidden-error'));
      } else {
        setGenericError(t('generic-error'));
      }
    } catch {
      setGenericError(t('generic-error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Both fields start empty, so an untouched form is not dirty and Escape
  // closes it without a discard prompt (IMM-856.1).
  const dirty = fullName !== '' || email !== '';

  return (
    <Modal
      open
      onClose={close}
      dirty={dirty}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('back-cta')}
          </Button>
          <Button type="submit" form="admin-create-form" disabled={submitting}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="admin-create-form" className="space-y-4" onSubmit={submit} noValidate>
        <FormField id="admin-full-name" label={t('full-name-label')} error={errors.fullName}>
          {(errId) => (
            <Input
              id="admin-full-name"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                clearError('fullName');
              }}
              placeholder={t('full-name-placeholder')}
              disabled={submitting}
              invalid={errors.fullName !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>
        <FormField id="admin-email" label={t('email-label')} error={errors.email}>
          {(errId) => (
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError('email');
              }}
              placeholder={t('email-placeholder')}
              disabled={submitting}
              invalid={errors.email !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>
        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}
